/** @jsxImportSource @emotion/react */
import React from "react";
import * as Icons from "@goodgym/icons";
import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import * as u from "@goodgym/util";
import _ from "lodash";
import * as C from "./components";
import CreateAddressForm from "./CreateAddressForm";
import { NewAddressData } from "./CreateAddressForm";

export type AddressSearchDatabaseProps = UI.TextFieldProps & {
  onAddressSelected: (
    address: T.AddressSearchDatabaseResultFragment | NewAddressData | null
  ) => any;
  onNewAddressCreated?: (newAddress: NewAddressData) => void;
  value?: T.AddressSearchDatabaseResultFragment | NewAddressData | null;
};

const AddressSearchDatabase: React.FC<React.PropsWithChildren<AddressSearchDatabaseProps>> = ({
  onAddressSelected,
  onNewAddressCreated,
  value,
  ...props
}) => {
  const [search, { loading, data }] = T.useAddressSearchDatabaseLazyQuery({});
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const throttledSearch = React.useMemo(
    () => _.debounce(search, 200),
    [search]
  );
  const [selectedAddress, setSelectedAddress] =
    React.useState<T.AddressSearchDatabaseResultFragment | NewAddressData | null>(null);
  
  React.useEffect(() => {
    if (value && !selectedAddress?.id) {
      setSelectedAddress(value);
    }
  }, [value, selectedAddress?.id]);

  const [key, setKey] = React.useState(_.uniqueId());
  const handleCreateNewAddress = (newAddressData: NewAddressData) => {
    if (onNewAddressCreated) onNewAddressCreated(newAddressData);
    setSelectedAddress(newAddressData);
    setShowCreateForm(false);
  };
   

  const options = React.useMemo(() => {
    const dbResults = data?.results || [];
    return selectedAddress &&
      !dbResults.some((opt) => opt.id === selectedAddress.id)
      ? [selectedAddress]
      : dbResults;
  }, [data?.results, selectedAddress]);
  return (
    <>
      {!showCreateForm ? (
        <>
          <UI.Autocomplete<
            T.AddressSearchDatabaseResultFragment | NewAddressData
          >
            key={key}
            placeholder="Start typing an address"
            isOptionEqualToValue={(option, value) => {
              return (
                option.id === value.id && option.__typename === value.__typename
              );
            }}
            getOptionLabel={(option) => u.text.compositeAddress(option) || ""}
            options={options}
            loading={loading}
            value={selectedAddress}
            onChange={(_e, address) => {
              if (address) {
                setSelectedAddress(address);
                onAddressSelected(address);
              } else {
                setSelectedAddress(null);
                onAddressSelected(null);
                setKey(_.uniqueId());
              }
            }}
            onInputChange={(e, value, reason) => {
              if (reason === "input") {
                throttledSearch({ variables: { query: value } });
              }
            }}
            clearOnBlur
            filterOptions={(x) => x}
            renderOption={(props, option) => (
              <li {...props} key={`address-${option.id}`}>
                <C.SearchResult address={option} />
              </li>
            )}
            renderInput={(params) => (
              <form autoComplete="off" style={{ display: "contents" }}>
                <UI.TextField
                  {...params}
                  {...props}
                  type="text"
                  inputMode="none"
                  autoComplete="off"
                  name="address-autofill-killer"
                  onChange={(e) => {
                    throttledSearch({ variables: { query: e.target.value } });
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <UI.CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              </form>
            )}
          />
          <UI.Box>
            <UI.Button
              type="button"
              variant="outlined"
              onClick={() => setShowCreateForm(true)}
              sx={{ my: 2 }}
            >
              Create New Address
            </UI.Button>
          </UI.Box>
        </>
      ) : (
        <CreateAddressForm
          onSaveAddress={handleCreateNewAddress}
          onCancel={() => setShowCreateForm(false)}
        />
      )}
    </>
  );
};
export default AddressSearchDatabase;
